import { connect } from 'react-redux';
import { useState } from 'react';
import { Stack } from '@optimus-web/core';
import { ResourceState, Reservation, ServiceOrderState } from '@mews-commander/data-access';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { checkIn } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { getReservationPersonCount } from '@mews-commander/legacy-entities/src/utils/reservations';
import { getRequirePreauthorizationForStart } from '@mews-commander/legacy-entities/src/ducks/services';
import { fetchCurrentEnterpriseReservationsByReservationIds } from '@mews-commander/legacy-entities/src/actions';
import { isCuttable, isStartable } from '../../selectors/reservationModuleState/reservationSelection';
import { withActiveWindowLoader } from '../../../../core';
import { KeyCutterButton } from '../KeyCutterButton/KeyCutterButton';
import { useFetchKeyCuttersQuery } from '../../queries/useFetchKeyCuttersQuery';
import ReservationCheckInAction from '../SmartDetail/ReservationCheckInAction';
import { RESERVATION_ORDER_ITEMS_EXTENT } from '../../constants';
import { getCurrentEnterpriseResourceState } from './selectors';

interface OwnProps {
    reservation: Reservation;
}
interface StateProps {
    assignedResourceState: ResourceState | undefined;
    isServicePreauthorizationEnabled: boolean;
}
interface DispatchProps {
    handleCheckIn: () => void;
    fetchReservation: () => void;
}
type Props = OwnProps & StateProps & DispatchProps;

const CheckInButton = ({
    reservation,
    assignedResourceState,
    isServicePreauthorizationEnabled,
    handleCheckIn,
    fetchReservation,
}: Props) => {
    const { assignedResourceId } = reservation;
    const [active, setDialogActive] = useState<boolean>(false);

    const { data: keyCuttersOptions = [] } = useFetchKeyCuttersQuery();

    const withCheckInButton = isStartable(reservation) && assignedResourceId !== null;
    const shouldCheckInDirectly =
        withCheckInButton &&
        !isServicePreauthorizationEnabled &&
        !keyCuttersOptions.length &&
        assignedResourceState === ResourceState.Inspected;
    const onButtonClick = shouldCheckInDirectly ? handleCheckIn : () => setDialogActive(true);
    const onClose = () => setDialogActive(false);

    return (
        <ReservationCheckInAction
            active={active}
            reservationId={reservation.id}
            onUpdate={fetchReservation}
            onButtonClick={onButtonClick}
            onClose={onClose}
            withCheckInButton={withCheckInButton}
        />
    );
};

const Actions = (props: Props) => {
    const { reservation } = props;

    const isCheckedIn = reservation.serviceOrderState === ServiceOrderState.Started;

    const keyCutterButton = isCuttable(reservation) ? (
        <KeyCutterButton
            isCheckedIn={isCheckedIn}
            reservationIds={[reservation.id]}
            initKeyCount={getReservationPersonCount(reservation)}
        />
    ) : null;

    return (
        <Stack spacing="100" alignment="end">
            {keyCutterButton}
            <CheckInButton {...props} />
        </Stack>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state, { reservation: { assignedResourceId, serviceId } }) => ({
        assignedResourceState: getCurrentEnterpriseResourceState(state, { resourceId: assignedResourceId }),
        isServicePreauthorizationEnabled: getRequirePreauthorizationForStart(state, { id: serviceId }) ?? false,
    }),
    (dispatch: CommanderThunkDispatch, { reservation: { id } }) => {
        const fetchReservation = async () =>
            dispatch(
                fetchCurrentEnterpriseReservationsByReservationIds({
                    reservationIds: [id],
                    extent: RESERVATION_ORDER_ITEMS_EXTENT,
                })
            );

        return {
            fetchReservation,
            handleCheckIn: async () =>
                withActiveWindowLoader(async () => {
                    await dispatch(checkIn([id]));
                    await fetchReservation();
                }),
        };
    }
)(Actions);
